// AppealModal.tsx — collects the written reason before appealing a decided case.
//
// README section 6: the member says why they believe the decision was wrong, and
// that explanation is the first thing the fresh team reads. The server enforces
// this (400 validation on a blank reason), so the reason is a real required
// field, not a courtesy — the appeal button used to POST an empty body and every
// appeal failed.
//
// Structure/accessibility/styling mirror ReportModal, which is the established
// dialog pattern in this codebase.

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from 'styled-components';
import { Dialog, Button, Textarea, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useCases } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface AppealModalProps {
  caseId: string;
  onClose: () => void;
}

// ─── Styled components ─────────────────────────────────────────────────────────

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBox = styled.div`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const InfoText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const FieldError = styled.p`
  margin: -8px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

const FormError = styled.div`
  background: ${({ theme }) =>
    `color-mix(in srgb, ${theme.color.danger} 14%, ${theme.color.glassBg})`};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const FormErrorText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.5;
`;

const Counter = styled.p<{ $over: boolean }>`
  margin: -8px 0 0;
  align-self: flex-end;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme, $over }) => ($over ? theme.color.danger : theme.color.textMuted)};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

export const APPEAL_REASON_MAX = 1000;

export function AppealModal({ caseId, onClose }: AppealModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Field-level (the reason itself was rejected) vs form-level (the request
  // could not be accepted for a reason the member cannot fix by editing text).
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { appeal } = useCases();
  const toast = useToast();

  const trimmed = reason.trim();
  const over = reason.length > APPEAL_REASON_MAX;
  const canSubmit = trimmed.length > 0 && !over && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setFieldError(null);
    setFormError(null);
    try {
      await appeal(caseId, trimmed);
      toast('Appeal submitted. A fresh team will hear it.');
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'validation') {
        setFieldError('Please say why you believe the decision was wrong.');
      } else if (e instanceof ApiError && e.code === 'no_fresh_team') {
        // NOT a rejection of the appeal — it is a scheduling problem. Every
        // member of the original team is excluded from hearing it again, and
        // right now there are not enough moderators left to form one.
        setFormError(
          'No independent team is available to hear your appeal yet. Your appeal has not been rejected. Please try again later.',
        );
      } else if (e instanceof ApiError && e.code === 'not_your_case') {
        setFormError('Only the person a case is about can appeal it.');
      } else {
        setFormError('Something went wrong submitting your appeal. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setReason(e.currentTarget.value);
    if (fieldError) setFieldError(null);
  }

  return (
    <Dialog open title="Appeal this decision" onClose={onClose}>
      <Stack>
        <InfoBox data-testid="appeal-what-happens-next">
          <Icon name="hourglass" size={16} title="Info" />
          <InfoText>
            <strong>What happens next:</strong> A completely fresh Council team is drawn to hear
            your appeal. Every member of the team that made the original decision is excluded.
            What you write here is the first thing they read.
          </InfoText>
        </InfoBox>

        <Textarea
          label="Why do you believe the decision was wrong?"
          placeholder="Explain why you believe the decision was wrong"
          value={reason}
          onChange={handleChange}
          rows={5}
          required
          aria-invalid={fieldError !== null}
          aria-describedby={fieldError ? 'appeal-reason-error' : undefined}
        />
        {fieldError && (
          <FieldError id="appeal-reason-error" role="alert">
            {fieldError}
          </FieldError>
        )}
        <Counter $over={over}>
          {reason.length} / {APPEAL_REASON_MAX}
        </Counter>

        {formError && (
          <FormError role="alert" data-testid="appeal-form-error">
            <Icon name="shield" size={16} title="Notice" />
            <FormErrorText>{formError}</FormErrorText>
          </FormError>
        )}

        <ButtonRow>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? 'Submitting…' : 'Submit appeal'}
          </Button>
        </ButtonRow>
      </Stack>
    </Dialog>
  );
}
